import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import {
  Box, Grid, Paper, Typography, LinearProgress,
  TextField, Button, Card, CardMedia, IconButton, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';

// --- AUTH IMPORTS ---
import { useAuth } from '../AuthProvider';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from '../authUtils';
const urlconfig = require('../config/urlconfig');

// --- DRAGGABLE PRODUCT CARD (Left Side) ---
const DraggableProduct = ({ product }) => {
  // Use ecomProductId if available, otherwise fallback to id or a random string to prevent crashes
  const uniqueId = product.ecomProductId ? `product-${product.ecomProductId}` : `product-local-${product.id}`;

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: uniqueId,
    data: product,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 9999,
  } : undefined;

  return (
    <Card
      ref={setNodeRef} style={style} {...listeners} {...attributes}
      sx={{ display: 'flex', mb: 2, p: 1, cursor: 'grab', '&:hover': { boxShadow: 3 } }}
    >
      <CardMedia
        component="img"
        sx={{ width: 60, height: 60, objectFit: 'contain' }}
        image={product.ecomThumbnailUrl || 'https://via.placeholder.com/60?text=No+Img'}
      />
      <Box sx={{ ml: 1, overflow: 'hidden' }}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }} noWrap>
          {product.productName}
        </Typography>
        <Typography variant="caption" color="primary">
          ₹{product.ecomMrp || product.lastPurchasePrice || 0}
        </Typography>
      </Box>
    </Card>
  );
};

// --- DROPPABLE GIFT BOX (Right Side) ---
const GiftBoxArea = ({ items, onRemove, budget, currentTotal }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'gift-box' });

  const fillPercent = Math.min((currentTotal / budget) * 100, 100);
  const isOverBudget = currentTotal > budget;

  return (
    <Paper
      ref={setNodeRef}
      elevation={isOver ? 6 : 2}
      sx={{
        height: '100%', minHeight: '600px', p: 3,
        backgroundColor: isOver ? '#e3f2fd' : '#fff',
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'grey.300',
        transition: 'all 0.2s ease',
        // Subtle background pattern when empty
        backgroundImage: items.length === 0 ? 'url("https://www.transparenttextures.com/patterns/cubes.png")' : 'none'
      }}
    >
      <Typography variant="h5" align="center" gutterBottom sx={{ color: '#d4af37', fontWeight: 'bold' }}>
        🎁 The Package
      </Typography>

      {/* BUDGET METER */}
      <Box sx={{ mb: 4, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Budget Usage</Typography>
          <Typography variant="body2" color={isOverBudget ? 'error' : 'success.main'} fontWeight="bold">
            ₹{currentTotal.toLocaleString()} / ₹{budget.toLocaleString()}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={fillPercent}
          color={isOverBudget ? 'error' : 'primary'}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>

      {/* ITEMS IN BOX */}
      <Grid container spacing={2}>
        {items.map((item, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f9f9f9', borderLeft: '4px solid #d4af37' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <img
                    src={item.ecomThumbnailUrl || 'https://via.placeholder.com/40'}
                    alt=""
                    style={{ width: 40, height: 40, marginRight: 10, objectFit: 'contain' }}
                />
                <Box>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{item.productName}</Typography>
                  <Typography variant="caption" fontWeight="bold">
                    ₹{item.ecomMrp || item.lastPurchasePrice || 0}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" color="error" onClick={() => onRemove(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        ))}

        {items.length === 0 && (
          <Box sx={{ width: '100%', textAlign: 'center', mt: 10, opacity: 0.5 }}>
            <Typography variant="h6" color="textSecondary">Drag items here</Typography>
            <Typography variant="body2">or click "AI Auto-Fill"</Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

// --- MAIN PARENT COMPONENT ---
const GiftBuilder = () => {
  const { account } = useAuth();
  const { instance } = useMsal();

  const [budget, setBudget] = useState(2000);
  const [boxItems, setBoxItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // 1. FETCH INVENTORY (Real API Call)
  const fetchInventory = useCallback(async (query = '') => {
    if (!account) return;
    setLoading(true);
    try {
      const token = await getApiToken(instance, account);
      // Calls your SmartSearchController endpoint
      const response = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/smart-search?query=${encodeURIComponent(query)}&size=20`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInventory(data.content || []);
      }
    } catch (error) {
      console.error("Failed to fetch inventory", error);
    } finally {
      setLoading(false);
    }
  }, [account, instance]);

  // Initial Load
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Handle Search Input Enter Key
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      fetchInventory(searchQuery);
    }
  };

  // 2. AI AUTO-FILL (Real API Call)
  const handleAiAutoFill = async () => {
    if (!account) return;
    setAiLoading(true);
    try {
      const token = await getApiToken(instance, account);
      // Calls your GiftCuratorController endpoint
      const response = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/gift-curator/generate?budget=${budget}`,
        {
            headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
          const bundles = await response.json();
          // The API returns a list of bundles (strategies). We pick the first one (Hero Strategy) by default.
          if (bundles && bundles.length > 0) {
            // Assign unique IDs to items to allow duplicates in UI if needed (though backend deduplicates)
            const itemsWithIds = bundles[0].map(item => ({
                ...item,
                uniqueUiId: Date.now() + Math.random()
            }));
            setBoxItems(itemsWithIds);
          } else {
            alert("AI couldn't find a perfect match. Try adjusting the budget!");
          }
      }
    } catch (error) {
      console.error("AI Generation failed", error);
      alert("AI Service is busy. Please try manual selection.");
    } finally {
      setAiLoading(false);
    }
  };

  // Drag & Drop Handlers
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && over.id === 'gift-box') {
      const newItem = active.data.current;
      // Add a unique ID so we can remove specific instances of the same item if dropped twice
      setBoxItems((prev) => [...prev, { ...newItem, uniqueUiId: Date.now() }]);
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = [...boxItems];
    newItems.splice(index, 1);
    setBoxItems(newItems);
  };

  // Calculate Total (using Ecom MRP or falling back to purchase price)
  const currentTotal = boxItems.reduce((sum, item) =>
    sum + (parseFloat(item.ecomMrp) || parseFloat(item.lastPurchasePrice) || 0), 0
  );

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Grid container spacing={2} sx={{ height: 'calc(100vh - 100px)', p: 2, bgcolor: '#f4f6f8' }}>

        {/* LEFT SIDE: INVENTORY */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Inventory</Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth size="small" placeholder="Search products..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                <Button variant="contained" onClick={() => fetchInventory(searchQuery)} sx={{ minWidth: 'auto', px: 2 }}>
                    <SearchIcon />
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
               {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                        <CircularProgress />
                    </Box>
               ) : (
                  inventory.map((p) => (
                    <DraggableProduct
                        // Key must be unique for React list rendering
                        key={p.ecomProductId || p.id || Math.random()}
                        product={p}
                    />
                  ))
               )}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT SIDE: BUILDER */}
        <Grid item xs={12} md={8}>
           <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', bgcolor: 'white', p: 2, borderRadius: 2, boxShadow: 1 }}>
              <TextField
                label="Target Budget (₹)" type="number" size="small"
                value={budget} onChange={(e) => setBudget(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleAiAutoFill}
                disabled={aiLoading}
                startIcon={aiLoading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                sx={{
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#45a049' },
                    color: 'white',
                    fontWeight: 'bold',
                    boxShadow: 2
                }}
              >
                {aiLoading ? "Curating..." : "AI Auto-Fill"}
              </Button>
           </Box>

           <GiftBoxArea
             items={boxItems}
             onRemove={handleRemoveItem}
             budget={budget}
             currentTotal={currentTotal}
           />
        </Grid>

      </Grid>
    </DndContext>
  );
};

export default GiftBuilder;